import { AuthenticationResult, EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ConfigProvider } from 'antd';
import React, { Suspense } from 'react';
import { FacebookProvider } from 'react-facebook';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import {
  BrowserRouter, Route, Routes
} from 'react-router-dom';
import './App.scss';

import { store } from './store';

// import Loading from 'components/atoms/Loading';
import { ToastProvider } from 'components/molecules/Toast';
import CheckoutWebsiteLayout from 'components/templates/CheckoutWebsiteLayout/index.';
import MainLayout from 'components/templates/MainLayout';
import MainlayoutChatbot from 'components/templates/MainlayoutChatbot';
import Chatting from 'components/templates/WSChatting';
import WebsiteLayout from 'components/templates/WebsiteLayout';
import { msalConfig } from 'configs/authConfig';
import Portal from 'navigations/Portal';
import ProtectedRoute from 'navigations/ProtectedRoute';
import AddNewPackage from 'pages/AddNewPackage';
import AddOnStatus from 'pages/AddNewPackage/PaymentStatus';
import Blog from 'pages/Blog';
import BlogDetail from 'pages/Blog/BlogDetail';
import NotFoundBlog from 'pages/Blog/NotFoundBlog';
import ChangePlan from 'pages/ChangePlan';
import ChangePlanStatus from 'pages/ChangePlan/PaymentStatus';
import ChatbotWidget from 'pages/ChatbotWidget';
// import Chatting from 'pages/Chatting';
import Checkout from 'pages/Checkout';
import PaymentStatus from 'pages/Checkout/PaymentStatus';
import ConversationHistory from 'pages/ConversationHistory';
import Home from 'pages/Home';
import Login from 'pages/Login';
import LoginGoogle from 'pages/LoginGoogle';
import LoginMicrosoft from 'pages/LoginMicrosoft';
import NewsDetail from 'pages/NewsDetail';
import Onboarding from 'pages/Onboarding';
import PaymentMethod from 'pages/PaymentMethod';
import PricingPlan from 'pages/PricingPlan';
import PrivacyPolicy from 'pages/PrivacyPolicy';
import RenewPackage from 'pages/RenewPackage';
import Tnc from 'pages/Tnc';
import useRoutesList from 'routes';
import { ROUTE_PATHS } from 'utils/constants';

import './i18n'; // import cấu hình i18n

const App: React.FC = () => {
  const { routes } = useRoutesList();

  return (
    // <Suspense fallback={<Loading width={50} variant="fullscreen" />}>
    <Suspense>
      <Routes>
        <Route path={ROUTE_PATHS.NOT_FOUND} element={<NotFoundBlog />} />
        <Route path={ROUTE_PATHS.CHATBOT_WIDGET} element={<ChatbotWidget />} />
        <Route path={ROUTE_PATHS.PAYMENT_METHOD} element={<PaymentMethod />} />
        <Route element={<CheckoutWebsiteLayout />}>
          <Route path={ROUTE_PATHS.CHECKOUT} element={<Checkout />} />
          <Route path={ROUTE_PATHS.CHANGE_PLAN} element={<ChangePlan />} />
          <Route path={ROUTE_PATHS.PAYMENT_STATUS} element={<PaymentStatus />} />
          <Route path={ROUTE_PATHS.ADD_NEW_STATUS} element={<AddOnStatus />} />
          <Route path={ROUTE_PATHS.CHANGE_PLAN_STATUS} element={<ChangePlanStatus />} />
          <Route path={ROUTE_PATHS.ADD_NEW_PACKAGE} element={<AddNewPackage />} />
          <Route path={ROUTE_PATHS.RENEW_PACKAGE} element={<RenewPackage />} />
        </Route>

        <Route element={<WebsiteLayout />}>
          <Route path={ROUTE_PATHS.HOME} element={<Home />} />
          <Route path={ROUTE_PATHS.PRICING} element={<PricingPlan />} />
          <Route path={ROUTE_PATHS.BLOG} element={<Blog />} />
          <Route path={ROUTE_PATHS.BLOG_DETAIL} element={<BlogDetail />} />
          <Route path={ROUTE_PATHS.NEWS} element={<NewsDetail />} />
          <Route path={ROUTE_PATHS.TNC} element={<Tnc />} />
          <Route path={ROUTE_PATHS.PRIVACY_POLICY} element={<PrivacyPolicy />} />
        </Route>

        <Route element={<MainlayoutChatbot />}>
          <Route path={ROUTE_PATHS.CONVERSATION_HISTORY} element={<ConversationHistory />} />
          <Route path={ROUTE_PATHS.CHATTING} element={<Chatting />} />
        </Route>
        <Route path={ROUTE_PATHS.ONBOARDING} element={<Onboarding />} />
        <Route element={<Portal />}>
          <Route path={ROUTE_PATHS.LOGIN_GOOGLE} element={<LoginGoogle />} />
          <Route path={ROUTE_PATHS.LOGIN_MICROSOFT} element={<LoginMicrosoft />} />
          <Route path={ROUTE_PATHS.LOGIN} element={<Login />} />
        </Route>

        <Route element={<MainLayout />}>
          {(Object.keys(routes) as Array<keyof typeof routes>).map((ele, index) => {
            if (routes[ele] && routes[ele].childrens) {
              return (
                <Route key={`router-${ele}-${index.toString()}`} element={routes[ele].element}>
                  {routes[ele].childrens?.map((child, childIndex) => (
                    <Route
                      key={`router-${ele}-${childIndex.toString()}`}
                      path={child.path}
                      element={
                        child.isProtected ? (
                          <ProtectedRoute path={child.path || ''}>{child.element}</ProtectedRoute>
                        ) : (
                          child.element
                        )
                      }
                    />
                  ))}
                </Route>
              );
            }
            return (
              <Route
                key={`router-${ele}-${index.toString()}`}
                path={routes[ele].path}
                element={
                  routes[ele].isProtected ? (
                    <ProtectedRoute path={routes[ele].path || ''}>{routes[ele].element}</ProtectedRoute>
                  ) : (
                    routes[ele].element
                  )
                }
              />
            );
          })}
        </Route>
      </Routes>
    </Suspense>
  );
};

const AppProvider: React.FC = () => {
  const msalInstance = new PublicClientApplication(msalConfig);

  // Default to using the first account if no account is active on page load
  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]); // also fixed here
  }

  // Listen for sign-in event and set active account
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      const payload = event.payload as AuthenticationResult;
      const account = payload?.account;
      if (account) {
        msalInstance.setActiveAccount(account);
      }
    }
  });

  const queryClient = React.useRef(
    new QueryClient({
      defaultOptions: {
        queries: {
          retry: 0,
          refetchOnMount: true,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        },
      },
    })
  );

  return (
    <Provider store={store}>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient.current}>
          <HelmetProvider>
            <BrowserRouter>
              <ConfigProvider
                theme={{
                  token: {
                    colorPrimary: '#f96619',
                    colorPrimaryHover: '#f96619',
                    colorPrimaryActive: '#f96619',
                    fontFamily: 'Open Sans',
                  },
                  components: {
                    Button: {
                      colorPrimary: '#f96619',
                      colorPrimaryHover: '#f96619',
                      colorPrimaryActive: '#f96619',
                    },
                    Modal: {
                      colorPrimary: '#f96619',
                      colorPrimaryHover: '#f96619',
                      colorPrimaryActive: '#f96619',
                      colorPrimaryBg: '#f96619',
                    },
                  },
                }}
              >
                <FacebookProvider
                  appId="1631001774266173"
                  version="v23.0" // Optional, defaults to latest
                  language="en_US" // Optional, defaults to en_US
                  lazy={false} // Optional, load SDK on demand
                  debug={false}
                >
                  <ToastProvider>
                    <App />
                  </ToastProvider>
                </FacebookProvider>
              </ConfigProvider>
            </BrowserRouter>
          </HelmetProvider>
        </QueryClientProvider>
      </MsalProvider>
    </Provider>
  );
};

export default AppProvider;
